import classNames from 'classnames';
import React from 'react';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import SVGIcon from '../../components/SVGIcon';
import downloadIcon from '../../icons/non-prefilled/16x16/download.svg';
import downloadAllIcon from '../../icons/non-prefilled/16x16/downloadAll.svg';
import newTabIcon from '../../icons/non-prefilled/16x16/newTab.svg';
import overflowMenuIcon from '../../icons/non-prefilled/16x16/overflowMenu.svg';
import attachmentIcon from '../../icons/prefilled/16x16/attachmentStroke.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import Logger from '../../utils/Logger';
import getExtname from '../../utils/url/getExtname';
import * as styles from './AttachmentItem.module.scss';
import IconButton from '../../components/IconButton';
import Tooltip from '../../components/Tooltip';
import type { AttachmentFile } from '../formats/Attachment';
import Link from '../../utils/router/Link';

const EXTENSIONS_FOR_NEW_TAB = [
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'txt',
  'md',
];

const getReadableSize = (bytes: number) => {
  let i = -1;
  let size = bytes;
  const byteUnits = ['KB', 'MB', 'GB'];
  do {
    size /= 1000;
    i += 1;
  } while (size > 1000 && i <= byteUnits.length);

  return Math.max(size, 0.1).toFixed(1) + byteUnits[i];
};

const getDownloadLink = ({
  source,
  name,
}: {
  source: string;
  name: string;
}) => {
  try {
    const url = new URL(source);
    url.searchParams.append('dl', name);
    return url.toString();
  } catch (err) {
    Logger.error('Invalid attachment source caught', source);
    return null;
  }
};

interface AttachmentProps {
  attachment: AttachmentFile;
  readOnly: boolean;
  isOverflowMenuOpen: boolean;
  onOverflowMenuOpenChange: (isOpen: boolean) => void;
  isAttachmentUploaded: (attachment: AttachmentFile) => boolean;
  onDeleteAttachment?: () => void;
  onDownloadAll: (() => void) | null;
  overflowMenuPortal?: HTMLElement;
}

const AttachmentItem = ({
  attachment,
  readOnly,
  onDeleteAttachment,
  isAttachmentUploaded,
  isOverflowMenuOpen,
  onOverflowMenuOpenChange,
  onDownloadAll,
  overflowMenuPortal,
}: AttachmentProps) => {
  const extname = getExtname(attachment.name);
  const isUploaded = isAttachmentUploaded(attachment);
  const downloadLink = isUploaded && getDownloadLink(attachment);

  const item = (
    <div className={styles.attachment}>
      <div
        data-no-print
        className={classNames(styles.type, styles[extname.toLowerCase()])}
      >
        {extname.slice(0, 4)}
        <div className={styles.icon}>
          <SVGIcon>{attachmentIcon}</SVGIcon>
        </div>
      </div>
      <div className={styles.name}>{attachment.name}</div>
      <div className={styles.size}>{getReadableSize(attachment.size)}</div>
      <div className={styles.source}>
        <Link to={attachment.source}>{attachment.source}</Link>
      </div>
    </div>
  );

  const menuItems = [];

  if (isUploaded && EXTENSIONS_FOR_NEW_TAB.includes(extname.toLowerCase())) {
    menuItems.push(
      <MenuItem
        key="openNewTab"
        icon={newTabIcon}
        to={attachment.source}
        target="_blank"
        rel="noreferrer"
      >
        Open in new tab
      </MenuItem>,
    );
  }

  if (onDownloadAll) {
    menuItems.push(
      <MenuItem
        key="downloadAll"
        icon={downloadAllIcon}
        onClick={onDownloadAll}
      >
        Download all (zip)
      </MenuItem>,
    );
  }

  if (!readOnly && onDeleteAttachment) {
    if (menuItems.length) {
      menuItems.push(<MenuSeparator key="separator" />);
    }
    menuItems.push(
      <MenuItem
        icon={trashIcon}
        key="delete"
        danger
        onClick={onDeleteAttachment}
      >
        Remove this file
      </MenuItem>,
    );
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.menuOpen]: isOverflowMenuOpen,
        [styles.uploading]: !isUploaded,
      })}
    >
      {item}

      {(downloadLink || menuItems.length > 0) && (
        <div data-no-print className={styles.menu}>
          {downloadLink && (
            <Tooltip content="Download file">
              <IconButton
                to={downloadLink}
                target="_blank"
                disabled={!isUploaded}
                rel="noreferrer"
                aria-label="Download file"
                className={styles.menuButton}
              >
                <SVGIcon>{downloadIcon}</SVGIcon>
              </IconButton>
            </Tooltip>
          )}
          {menuItems.length > 0 && (
            <OverflowMenu
              open={isOverflowMenuOpen}
              onOpenChange={onOverflowMenuOpenChange}
              placement="bottom-start"
              portal={overflowMenuPortal}
              button={
                <IconButton
                  className={styles.menuButton}
                  aria-label="More options"
                >
                  <SVGIcon>{overflowMenuIcon}</SVGIcon>
                </IconButton>
              }
            >
              {menuItems}
            </OverflowMenu>
          )}
        </div>
      )}
    </div>
  );
};

export default AttachmentItem;
